/**
 * SyproFlow Login Page — Email OTP Authentication
 * =================================================
 * Two-step flow: enter email → enter 6-digit OTP → authenticated.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Mail, ArrowLeft, Loader2, Shield } from 'lucide-react';

const API_BASE = (() => {
  if (typeof window !== 'undefined' && window.SYPROFLOW_CONFIG?.API_BASE_URL) {
    return window.SYPROFLOW_CONFIG.API_BASE_URL;
  }
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('syproflow_api_base');
    if (stored) return stored;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return 'http://localhost:8000';
})();

export default function LoginPage({ onLogin }) {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_BASE}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Failed to send OTP. Please try again.');
        return;
      }

      setSuccess('OTP sent to your email');
      setStep('otp');
      setResendCooldown(60);
      // Focus first OTP input after render
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // single digit
    setOtp(newOtp);

    // Auto-advance to next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newOtp.every(d => d !== '')) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      const newOtp = pasted.split('');
      setOtp(newOtp);
      otpRefs.current[5]?.focus();
      handleVerifyOTP(pasted);
    }
  };

  const handleVerifyOTP = async (otpString) => {
    const code = otpString || otp.join('');
    if (code.length !== 6) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: code }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
        return;
      }

      // Store token and email
      localStorage.setItem('syproflow_token', data.access_token);
      localStorage.setItem('syproflow_email', data.email);

      onLogin(data);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Failed to resend OTP.');
        return;
      }

      setSuccess('New OTP sent to your email');
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setOtp(['', '', '', '', '', '']);
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#ea580c', letterSpacing: '-0.03em' }}>
            SyproFlow
          </h1>
          <p className="text-sm text-slate-500 mt-1">Documents In. Data Out.</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          {step === 'email' ? (
            <>
              <h2 className="text-xl font-semibold text-slate-800 mb-1">Welcome back</h2>
              <p className="text-sm text-slate-500 mb-6">Enter your email to receive a login code</p>

              <form onSubmit={handleSendOTP}>
                <div className="relative mb-4">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoFocus
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition"
                  />
                </div>

                {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)' }}
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                    </span>
                  ) : 'Send OTP'}
                </button>
              </form>
            </>
          ) : (
            <>
              <button
                onClick={goBackToEmail}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4 transition"
              >
                <ArrowLeft className="w-3 h-3" /> Change email
              </button>

              <h2 className="text-xl font-semibold text-slate-800 mb-1">Enter your code</h2>
              <p className="text-sm text-slate-500 mb-6">
                We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>
              </p>

              {/* 6-digit OTP inputs */}
              <div className="flex gap-2 justify-center mb-4" onPaste={handleOTPPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPChange(i, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold rounded-xl border-2 border-slate-200 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition"
                    style={{ caretColor: '#ea580c' }}
                  />
                ))}
              </div>

              {error && <p className="text-red-500 text-xs text-center mb-3">{error}</p>}
              {success && <p className="text-green-600 text-xs text-center mb-3">{success}</p>}

              <button
                onClick={() => handleVerifyOTP()}
                disabled={loading || otp.some(d => d === '')}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)' }}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                  </span>
                ) : 'Verify'}
              </button>

              {/* Resend */}
              <div className="text-center mt-4">
                {resendCooldown > 0 ? (
                  <p className="text-xs text-slate-400">Resend code in {resendCooldown}s</p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium transition"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <Shield className="w-3.5 h-3.5 text-green-500" />
          <span>Your data is encrypted and never shared</span>
        </div>
      </div>
    </div>
  );
}
